import { Vector3 } from "three";

export interface BladeData {
  active: boolean;
  tipPrev: Vector3;
  tipCurr: Vector3;
  speed: number;
  // Speed spike vs. the recent moving average — the tenouchi "snap" signal,
  // computed in Katana.tsx alongside speed. See SNAP_WINDOW_SAMPLES.
  snap: number;
  pulse?: (grade: string, tenouchi: boolean) => void;
}

const blades = new Map<string, BladeData>();

export function registerBlade(id: string, data: BladeData) {
  blades.set(id, data);
}

export function unregisterBlade(id: string) {
  blades.delete(id);
}

// Pulses every registered controller's haptics, regardless of `active`
// state — used for events that hit the player rather than the blade (e.g.
// Obstacle.tsx's dodge/duck failures), where there's no single contacting
// blade to target and both hands should feel it. Unlike checkCollision,
// this ignores `active` on purpose: a Katana stays registered even in
// single-wield mode for the off-hand, and an obstacle hit should still jolt
// that controller too.
export function pulseAllBlades(grade: string) {
  for (const blade of blades.values()) blade.pulse?.(grade, false);
}

// Scratch vectors reused across calls — this module is only ever driven
// synchronously from the R3F frame loop (one blade/block pair at a time,
// fully consumed before the next call), so sharing these is safe and avoids
// allocating on every collision check, which runs every frame per block.
const scratchAB = new Vector3();
const scratchAP = new Vector3();
const scratchClosest = new Vector3();
const scratchCutDir = new Vector3();

export function distToSegment(p: Vector3, a: Vector3, b: Vector3): number {
  scratchAB.copy(b).sub(a);
  const lenSq = scratchAB.lengthSq();
  if (lenSq === 0) return p.distanceTo(a);
  scratchAP.copy(p).sub(a);
  const t = Math.max(0, Math.min(1, scratchAP.dot(scratchAB) / lenSq));
  scratchClosest.copy(a).addScaledVector(scratchAB, t);
  return p.distanceTo(scratchClosest);
}

export interface HitResult {
  bladeId: string;
  speed: number;
  snap: number;
  distance: number;
  cutDir: Vector3; // normalised direction of the slash
  pulse?: (grade: string, tenouchi: boolean) => void;
}

export function checkCollision(
  blockPos: Vector3,
  radius: number,
  requiredHand?: "left" | "right" | "any",
): HitResult | null {
  let activeCount = 0;
  for (const blade of blades.values()) {
    if (blade.active) activeCount++;
  }

  for (const [id, blade] of blades) {
    if (!blade.active) continue;

    // Enforce authored hand only when more than one blade can hit. In
    // single-wield, the active controller must be able to clear every target.
    if (activeCount > 1 && requiredHand && requiredHand !== "any") {
      const bladeHand = id.includes("right") ? "right" : "left";
      if (bladeHand !== requiredHand) continue;
    }

    const dist = distToSegment(blockPos, blade.tipPrev, blade.tipCurr);
    if (dist <= radius) {
      scratchCutDir.copy(blade.tipCurr).sub(blade.tipPrev).normalize();
      return {
        bladeId: id,
        speed: blade.speed,
        snap: blade.snap,
        distance: dist,
        cutDir: scratchCutDir,
        pulse: blade.pulse,
      };
    }
  }
  return null;
}
